import { useForgotPassword } from "@features/auth/api/use-forgot-password";
import { Button } from "@ui/button/button";
import { Input } from "@ui/input/input";
import { Modal } from "@ui/modal/modal";
import type { FormEvent, FunctionComponent } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { loginSchema } from "../login/login.schema";
import styles from "./reset-password-modal.module.scss";

interface Props {
	show: boolean;
	handleShow: () => void;
}

export const ResetPasswordModal: FunctionComponent<Props> = ({
	show,
	handleShow,
}) => {
	const { t } = useTranslation();
	const { forgotPasswordMutate, forgotPasswordMutatePending } =
		useForgotPassword();

	const [email, setEmail] = useState("");
	const [error, setError] = useState<string | null>(null);

	function handleSubmit(e: FormEvent) {
		e.preventDefault();
		// Reuses the login form's email rule so the check and the message it
		// renders can't drift apart.
		const result = loginSchema.shape.email.safeParse(email);

		if (!result.success) {
			setError(result.error.issues[0].message);
			return;
		}

		forgotPasswordMutate(
			{ email: result.data },
			{ onSuccess: () => handleShow() },
		);
	}

	useEffect(() => {
		if (show) return;
		setEmail("");
		setError(null);
	}, [show]);

	return (
		<Modal
			open={show}
			handleOpen={handleShow}
			title={t("resetPassword.title_email_state")}
			subtitle={t("resetPassword.subtitle_email_state")}
		>
			<div className={styles.modal_container}>
				<Modal.Body>
					<form
						onSubmit={handleSubmit}
						id="reset-form"
						className={styles.body_container}
					>
						<Input
							data-testid="reset-password-email-input"
							label={t("form.email.label")}
							placeholder={t("form.email.placeholder")}
							error={Boolean(error)}
							errorText={error ?? ""}
							value={email}
							onFocus={() => setError(null)}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</form>
				</Modal.Body>
				<Modal.Footer>
					<Button
						data-testid="reset-password-submit-btn"
						form="reset-form"
						className={styles.email_state_btn}
						type="submit"
						disabled={forgotPasswordMutatePending}
						isLoading={forgotPasswordMutatePending}
					>
						{t("base.reset")}
					</Button>
				</Modal.Footer>
			</div>
		</Modal>
	);
};
